export const CATEGORY_NAMES = {
  preflop: "Preflop",
  postflop: "Postflop",
  cash: "Cash Game",
  tournament: "Tournament",
  position: "Position",
  stack_depth: "Stack Depth"
};

export const POSITION_MAP = {
  "UTG": "UTG", "UTG+1": "HJ", "MP": "HJ", "LJ": "HJ",
  "HJ": "HJ", "CO": "CO", "BTN": "BTN", "BU": "BTN",
  "SB": "SB", "BB": "BB", "SB/BTN": "BTN"
};

export const TABLE_POSITIONS = ["UTG", "HJ", "CO", "BTN", "SB", "BB"];
export const TABLE_COORDS = [
  { x: 50, y: 88 }, { x: 15, y: 65 }, { x: 15, y: 30 },
  { x: 50, y: 12 }, { x: 85, y: 30 }, { x: 85, y: 65 }
];

export const POKER_GLOSSARY = {
  "3-bet": "A re-raise. The first raise is a 'bet', the second raise is a '2-bet', and the third is a '3-bet'.",
  "4-bet": "A raise after a 3-bet. Usually indicates a very strong hand or a bold bluff.",
  "5-bet": "A raise after a 4-bet. Extremely rare and almost always represents the nuts or a huge bluff.",
  "c-bet": "Continuation bet. A bet made by the preflop raiser on the flop to maintain aggression.",
  "check-raise": "Checking with the intention of raising after an opponent bets. A powerful deceptive play.",
  "donk bet": "A bet made by the out-of-position player into the preflop raiser, usually considered non-standard.",
  "float": "Calling a bet with a weak hand in position, planning to take the pot on a later street.",
  "fold equity": "The value gained from the chance your opponent will fold to your bet or raise.",
  "GTO": "Game Theory Optimal. A strategy that cannot be exploited, based on mathematical equilibrium.",
  "ICM": "Independent Chip Model. Assigns real-money value to tournament chips based on prize structure.",
  "implied odds": "The expected future earnings if you hit your draw, beyond the current pot odds.",
  "isolation": "Raising to get heads-up against a weaker player who limped into the pot.",
  "limp": "Calling the big blind preflop instead of raising. Generally considered passive play.",
  "nut advantage": "Having more premium hands (nuts) in your range than your opponent on a given board.",
  "overbet": "Betting more than the size of the pot. Used for polarized value bets or bluffs.",
  "pot control": "Keeping the pot small with a medium-strength hand to avoid difficult decisions.",
  "probe bet": "A bet made on a later street when the preflop aggressor checked the previous street.",
  "range": "The collection of all possible hands a player could hold in a given situation.",
  "semi-bluff": "Betting with a drawing hand that can improve, combining fold equity with equity when called.",
  "set mining": "Calling preflop with a small pair hoping to hit three of a kind (a set) on the flop.",
  "squeeze": "A large re-raise preflop after an open and one or more callers.",
  "steal": "Raising from late position to win the blinds without a fight.",
  "thin value": "Betting a marginal hand for value against a narrow range of worse hands.",
  "triple barrel": "Betting on flop, turn, and river. A complete bluff line or strong value.",
  "value bet": "Betting with a hand you believe is best, to get called by worse hands.",
  "block bet": "A small bet designed to prevent your opponent from making a larger bet.",
  "bluff catch": "Calling with a marginal hand specifically to catch an opponent's bluff.",
  "bubble": "The point in a tournament just before the money. ICM pressure is highest.",
  "double barrel": "Betting the flop and turn as the preflop raiser.",
  "effective stack": "The smaller of two players' stacks, determining the maximum amount at risk.",
  "equity": "Your mathematical share of the pot based on your chances of winning.",
  "exploitation": "Deviating from GTO to target specific weaknesses in an opponent's strategy.",
  "heads-up": "Playing against one opponent. Ranges widen dramatically.",
  "multiway": "A pot with three or more players. Hand values and strategies change significantly.",
  "oop": "Out of Position. Acting first in a betting round, a disadvantage in poker.",
  "position": "Your seat relative to the dealer. Later position = more information = bigger advantage.",
  "pot odds": "The ratio of the current pot to the cost of a call. Used to determine profitable calls.",
  "push/fold": "A strategy for very short stacks where the only options are all-in or fold.",
  "resteal": "Re-raising a suspected steal attempt, usually from the blinds.",
  "shove": "Going all-in. Pushing all your chips into the pot.",
  "straddle": "A voluntary blind bet, usually 2x the big blind, made before cards are dealt.",
  "suited connectors": "Two cards of the same suit in sequence (e.g., 7h-8h). Great for making straights and flushes.",
  "BB": "Big Blind. The larger of the two forced bets, and a unit of measurement for stack sizes.",
  "BTN": "Button. The dealer position, the most profitable seat at the table.",
  "CO": "Cutoff. The position one seat right of the button. Second-best position.",
  "HJ": "Hijack. Two seats right of the button. A middle-late position.",
  "SB": "Small Blind. Half the big blind, in the worst position postflop.",
  "UTG": "Under the Gun. The first player to act preflop. Requires the tightest range.",
  "SPR": "Stack-to-Pot Ratio. Remaining stack divided by pot size. Guides postflop commitment."
};

export const GTO_CHARTS = {
  utg: {
    label: "UTG Open (9-max)",
    desc: "Tightest opening range. Only premium hands.",
    raise: ["AA","KK","QQ","JJ","TT","99","88","77","AKs","AQs","AJs","ATs","A5s","A4s","KQs","KJs","KTs","QJs","QTs","JTs","AKo","AQo"]
  },
  co: {
    label: "Cutoff Open (9-max)",
    desc: "Wider range with position advantage.",
    raise: ["AA","KK","QQ","JJ","TT","99","88","77","66","55","44","AKs","AQs","AJs","ATs","A9s","A8s","A7s","A6s","A5s","A4s","A3s","A2s","KQs","KJs","KTs","K9s","K8s","QJs","QTs","Q9s","JTs","J9s","T9s","T8s","98s","87s","76s","65s","AKo","AQo","AJo","ATo","A9o","KQo","KJo","KTo","QJo","QTo","JTo"]
  },
  btn: {
    label: "Button Open (9-max)",
    desc: "Widest opening range. Steal the blinds aggressively.",
    raise: ["AA","KK","QQ","JJ","TT","99","88","77","66","55","44","33","22","AKs","AQs","AJs","ATs","A9s","A8s","A7s","A6s","A5s","A4s","A3s","A2s","KQs","KJs","KTs","K9s","K8s","K7s","K6s","K5s","K4s","QJs","QTs","Q9s","Q8s","Q7s","JTs","J9s","J8s","T9s","T8s","T7s","98s","97s","87s","86s","76s","75s","65s","64s","54s","53s","43s","AKo","AQo","AJo","ATo","A9o","A8o","A7o","A6o","A5o","A4o","A3o","A2o","KQo","KJo","KTo","K9o","K8o","QJo","QTo","Q9o","JTo","J9o","T9o","98o","87o"]
  },
  sb: {
    label: "Small Blind vs BB",
    desc: "Raise or fold. Limping is generally incorrect.",
    raise: ["AA","KK","QQ","JJ","TT","99","88","77","66","55","44","33","22","AKs","AQs","AJs","ATs","A9s","A8s","A7s","A6s","A5s","A4s","A3s","A2s","KQs","KJs","KTs","K9s","K8s","K7s","K6s","K5s","K4s","K3s","K2s","QJs","QTs","Q9s","Q8s","Q7s","Q6s","Q5s","JTs","J9s","J8s","J7s","T9s","T8s","T7s","98s","97s","96s","87s","86s","76s","75s","65s","64s","54s","53s","43s","AKo","AQo","AJo","ATo","A9o","A8o","A7o","A6o","A5o","A4o","A3o","A2o","KQo","KJo","KTo","K9o","K8o","K7o","K6o","QJo","QTo","Q9o","Q8o","JTo","J9o","J8o","T9o","T8o","98o","97o","87o","76o","65o"]
  }
};

export function parsePositionActions(actionHistory, heroSeat) {
  const posActions = {};
  const actionKeywords = Object.keys(POSITION_MAP);
  (actionHistory || []).forEach(action => {
    const lower = action.toLowerCase();
    if (lower.includes("folds to") || lower.includes("fold to")) return;
    let matchedSeat = null;
    let shortAction = action;
    for (const key of actionKeywords) {
      if (action.includes(key) || lower.includes(key.toLowerCase())) {
        matchedSeat = POSITION_MAP[key];
        shortAction = action.replace(new RegExp(key + "\\s*", "gi"), "").replace(/^(from |in |on )/i, "").trim();
        break;
      }
    }
    if (!matchedSeat && /hero/i.test(action)) {
      matchedSeat = heroSeat;
      shortAction = action.replace(/Hero\s*/gi, "").replace(/^(from |in |on )/i, "").trim();
    }
    if (matchedSeat && shortAction) {
      shortAction = shortAction
        .replace(/raises?\s+to\s+/i, "Raise ").replace(/3-bets?\s+to\s+/i, "3-bet ")
        .replace(/4-bets?\s+to\s+/i, "4-bet ").replace(/calls?\s*/i, "Call")
        .replace(/checks?\s*/i, "Check").replace(/folds?\s*/i, "Fold")
        .replace(/shoves?\s*/i, "Shove").replace(/limps?\s*/i, "Limp").replace(/bets?\s+/i, "Bet ");
      posActions[matchedSeat] = shortAction.substring(0, 18);
    }
  });
  return posActions;
}

export function resolveHeroSeat(heroRaw) {
  return POSITION_MAP[heroRaw] || POSITION_MAP[heroRaw?.split(" ")[0]] || heroRaw;
}

export function resolveVillainSeats(villainRaw) {
  const seats = new Set();
  Object.keys(POSITION_MAP).forEach(key => {
    if (villainRaw?.includes(key)) seats.add(POSITION_MAP[key]);
  });
  return seats;
}
